import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { useThematicMaps } from '@/hooks/useThematicMaps';
import { useWebsites } from '@/hooks/useWebsites';

export function NewMapDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [websiteId, setWebsiteId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { createMap } = useThematicMaps();
  const { websites } = useWebsites();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setCreating(true);
    const map = await createMap({
      name: name.trim(),
      website_id: websiteId || null,
      macro_statement: '',
      scope_in: [],
      scope_out: [],
      audience: '',
      language: 'pl',
      map_version: 'v1',
      status: 'draft',
    });

    setCreating(false);

    if (map) {
      setOpen(false);
      setName('');
      setWebsiteId('');
      navigate(`/mapy-tematyczne/${map.id}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Nowa mapa
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nowa mapa tematyczna</DialogTitle>
          <DialogDescription>Podaj nazwę i opcjonalnie przypisz domenę.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="map-name">Nazwa mapy</Label>
            <Input
              id="map-name"
              placeholder="np. Wole Sushi — SCN"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="map-website">Domena (opcjonalnie)</Label>
            <Select value={websiteId} onValueChange={setWebsiteId}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz domenę..." />
              </SelectTrigger>
              <SelectContent>
                {websites.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name} ({w.url})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button type="submit" disabled={creating || !name.trim()}>
              {creating ? 'Tworzenie...' : 'Utwórz mapę'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
